import {
  forwardRef,
  type KeyboardEvent,
  type KeyboardEventHandler,
} from 'react';

import type { RelativeAbsolutePosition } from '../../types/pointer-events.js';
import { SELECTION_RIGHT_OVERFLOW } from '../interactions-overlay/constants.js';
import { adjustPosition } from '../interactions-overlay/utils/adjust-position.js';
import { normalizeSelectionArea } from '../interactions-overlay/utils/normalize-selection-area.js';
import { SelectionAreaPathRefContext } from './context/selection-area-path-ref.context.js';
import { Selection } from './Selection.js';
import { brushOverlay } from './SelectionArea.sty.js';
import type { _SelectionRef, SelectionAreaVariant } from './types.js';
import { calculateSelectionDelta } from './utils/calculate-selection-delta.js';
import { clampSelectionArea } from './utils/clamp-selection-area.js';
import { moveSelectionArea } from './utils/move-selection-area.js';
import { ZoomSelection } from './ZoomSelection.js';

export interface SelectionAreaProps {
  bounds: DOMRect;
  selection: [RelativeAbsolutePosition, RelativeAbsolutePosition];
  variant: SelectionAreaVariant;
  focusable: boolean;
  interactable: boolean;
  bottomFormatter?: (value: number) => string;
  topFormatter?: (value: number) => string;
  onKeyDown: KeyboardEventHandler;
  onSelectionChange: ({
    selection,
    accessible,
  }: {
    selection: [RelativeAbsolutePosition, RelativeAbsolutePosition];
    accessible: boolean;
  }) => void;
  isBottomLabelRotated?: boolean;
  isTopLabelRotated?: boolean;
}

export const SelectionArea = forwardRef<
  _SelectionRef | null,
  SelectionAreaProps
>((props: SelectionAreaProps, forwardedRef) => {
  const {
    bounds,
    selection,
    variant,
    focusable,
    interactable,
    bottomFormatter,
    topFormatter,
    onKeyDown: handleWrapperKeyDown,
    onSelectionChange,
    isBottomLabelRotated = false,
    isTopLabelRotated = false,
  } = props;

  const [start, end] = selection;

  const { width, height } = bounds;

  const startX = start.relative.x;
  const endX = end.relative.x;

  const range: [number, number] = [0, width + SELECTION_RIGHT_OVERFLOW];

  const handleSelectionAreaKeyDown = (event: KeyboardEvent) => {
    const delta = calculateSelectionDelta(event, width);

    const nextSelection = clampSelectionArea(
      moveSelectionArea(selection, delta),
      range,
    );

    onSelectionChange?.({ selection: nextSelection, accessible: true });
  };

  const handleSelectionAreaStartKeyDown = (event: KeyboardEvent) => {
    const delta = calculateSelectionDelta(event, width);

    const adjustedStart = adjustPosition(start, delta);

    const nextSelection = clampSelectionArea(
      normalizeSelectionArea([adjustedStart, end]),
      range,
    );

    onSelectionChange?.({ selection: nextSelection, accessible: true });
  };

  const handleSelectionAreaEndKeyDown = (event: KeyboardEvent) => {
    const delta = calculateSelectionDelta(event, width);

    const adjustedEnd = adjustPosition(end, delta);

    const nextSelection = clampSelectionArea(
      normalizeSelectionArea([start, adjustedEnd]),
      range,
    );

    onSelectionChange?.({ selection: nextSelection, accessible: true });
  };

  return (
    <svg
      className={brushOverlay}
      onKeyDown={handleWrapperKeyDown}
      onBlur={(event) => event.stopPropagation()}
    >
      <SelectionAreaPathRefContext.Provider value={forwardedRef}>
        {variant === 'zoom' ? (
          <ZoomSelection
            bounds={bounds}
            focusable={focusable}
            interactable={interactable}
            bottomFormatter={bottomFormatter}
            topFormatter={topFormatter}
            startX={startX}
            endX={endX}
            height={height}
            handleSelectionAreaKeyDown={handleSelectionAreaKeyDown}
            handleSelectionAreaEndKeyDown={handleSelectionAreaEndKeyDown}
            handleSelectionAreaStartKeyDown={handleSelectionAreaStartKeyDown}
            isBottomLabelRotated={isBottomLabelRotated}
            isTopLabelRotated={isTopLabelRotated}
          />
        ) : (
          <Selection
            bounds={bounds}
            focusable={focusable}
            interactable={interactable}
            bottomFormatter={bottomFormatter}
            topFormatter={topFormatter}
            startX={startX}
            endX={endX}
            height={height}
            width={width}
            handleSelectionAreaEndKeyDown={handleSelectionAreaEndKeyDown}
            handleSelectionAreaStartKeyDown={handleSelectionAreaStartKeyDown}
            start={start}
            end={end}
            onSelectionChange={onSelectionChange}
            isBottomLabelRotated={isBottomLabelRotated}
            isTopLabelRotated={isTopLabelRotated}
          />
        )}
      </SelectionAreaPathRefContext.Provider>
    </svg>
  );
});
